import React, { useCallback, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { Divider, Flex, HStack, Image } from '@televet/kibble-ui/build/chakra';
import { Button, Link, Text, useToast } from '@televet/kibble-ui';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  useGetNonItemizedReceiptPdfLazyQuery,
  useGetReceiptPetParentQuery,
  useGetStripeReceiptQuery,
} from 'shared/types/graphql';
import { getCardBrandIcon } from 'pages/Conversations/components/ChannelMessage/MessageBubble/utils';
import { formatPhoneNumber, getMonetaryDisplayValue } from 'shared/utils';
import format from 'date-fns/format';
import { GraphQLFetchPolicies } from 'shared/enums';
import { convertDataToUrl, dataUrlToFile, downloadPdf } from 'pages/Conversations/utils/files';
import UAParser from 'ua-parser-js';
import useIsMobile from 'shared/hooks/useIsMobile';

interface NonItemizedReceiptProps {
  clinicPetParentId: string;
  intentId: string;
  stripeReceiptId?: string;
  paymentIdentifier?: string;
  paymentAmount: number;
  paymentMethod: string[];
  paymentDate?: Date;
  clientServiceFee: number;
}

const NonItemizedReceipt = ({
  clinicPetParentId,
  intentId,
  stripeReceiptId,
  paymentIdentifier,
  paymentAmount,
  paymentDate,
  paymentMethod,
  clientServiceFee,
}: NonItemizedReceiptProps): JSX.Element => {
  const { currentClinic } = useClinicUser();
  const toast = useToast();
  const { isMobile } = useIsMobile();
  const [isDownloading, setIsDownloading] = useState(false);

  const { data: petParentData } = useGetReceiptPetParentQuery({
    variables: {
      clinicPetParentId,
    },
    skip: !clinicPetParentId,
  });

  const { data: stripeReceiptData } = useGetStripeReceiptQuery({
    variables: { id: stripeReceiptId || '' },
    skip: !stripeReceiptId,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const nav = navigator as any;
  const isSharingAvailable = useMemo(() => nav.share !== undefined && !!isMobile, [isMobile, nav.share]);

  const { getBrowser } = new UAParser();
  const browser: string = getBrowser().name || '';
  // Safari and Firefox shows a blank page when the pdf is downloaded immediately, so we need to wait a bit before downloading the pdf
  const delayTime = browser === 'Safari' || browser === 'Firefox' ? 5000 : 0;

  const openPrintWindow = useCallback(
    async (source: string, fileName: string) => {
      const printWindow = document.createElement('iframe');
      printWindow.style.visibility = 'hidden';
      printWindow.id = 'printWindow';

      const src = await convertDataToUrl(source);
      printWindow.src = src;

      document.body.appendChild(printWindow);

      setTimeout(() => {
        if (printWindow.contentWindow) {
          printWindow.contentWindow.focus();
          printWindow.contentWindow.print();
        } else {
          downloadPdf(source, fileName);
        }
        setIsDownloading(false);
      }, delayTime);
    },
    [delayTime],
  );

  const [getPaymentReceiptPdf, { loading: isPDFLoading }] = useGetNonItemizedReceiptPdfLazyQuery({
    variables: {
      intentId,
    },
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
    onCompleted: async (data) => {
      const { base64PDF } = data?.exportNonItemizedReceiptAsPdf;
      const source = `data:application/pdf;base64,${base64PDF}`;
      const date = paymentDate || new Date();
      const fileName = `Receipt_${paymentIdentifier ? paymentIdentifier : new Date(date).toISOString()}`;
      const file = await dataUrlToFile(source, fileName, 'application/pdf');

      if (isSharingAvailable && nav.canShare && nav.canShare({ files: [file] })) {
        nav
          .share({
            files: [file],
          })
          .then(() => {
            toast({
              title: 'Successfully shared receipt.',
              status: 'success',
              duration: 2000,
              position: 'top',
            });
            setIsDownloading(false);
          })
          .catch((e: Error) => {
            console.error('Error sharing receipt', e);
            openPrintWindow(source, fileName);
          });
      } else {
        openPrintWindow(source, fileName);
      }
    },
    onError: (error) => {
      setIsDownloading(false);
      Sentry.captureException(error);
      toast({
        title: 'Error sharing receipt.',
        status: 'error',
        duration: 2000,
        position: 'top',
      });
    },
  });

  const petParent = useMemo(() => petParentData?.findUniqueClinicPetParent, [petParentData?.findUniqueClinicPetParent]);

  const petParentName = useMemo(() => `${petParent?.lastName}, ${petParent?.firstName}`, [petParent]);

  const petParentPhone = useMemo(() => formatPhoneNumber(petParent?.phoneNumbers[0]?.number), [petParent]);

  const totalBeforeFeeDisplay = useMemo(
    () => getMonetaryDisplayValue(paymentAmount - clientServiceFee, true),
    [paymentAmount, clientServiceFee],
  );

  const clientServiceFeeDisplay = useMemo(() => getMonetaryDisplayValue(clientServiceFee, true), [clientServiceFee]);

  const paymentAmountDisplay = useMemo(() => getMonetaryDisplayValue(paymentAmount, true), [paymentAmount]);

  const applicationName = useMemo(
    () => stripeReceiptData?.findUniqueStripeReceipt?.applicationName,
    [stripeReceiptData?.findUniqueStripeReceipt?.applicationName],
  );

  const fileName = useMemo(
    () => stripeReceiptData?.findUniqueStripeReceipt?.dedicatedFileName,
    [stripeReceiptData?.findUniqueStripeReceipt?.dedicatedFileName],
  );

  const accountType = useMemo(
    () => stripeReceiptData?.findUniqueStripeReceipt?.accountType,
    [stripeReceiptData?.findUniqueStripeReceipt?.accountType],
  );

  const handleDownloadPdf = useCallback(() => {
    setIsDownloading(true);
    getPaymentReceiptPdf();
  }, [getPaymentReceiptPdf]);

  return (
    <Flex direction="column" p={6}>
      <Flex direction="column" w="100%">
        <Text my={5} fontWeight="bold">
          {`Receipt ${!!paymentIdentifier ? `#${paymentIdentifier}` : ''}`}
        </Text>
        <Divider />
      </Flex>
      <Image mt={4} src={currentClinic?.logo || ''} w="56px" />
      <Flex my={2} w="100%" justifyContent="space-between">
        <Flex direction="column">
          <Text mt={4} mb={3} fontWeight="bold">
            {currentClinic?.name}
          </Text>
          <Flex direction="column">
            <Text size="sm">{currentClinic?.addressLine1}</Text>
            <Text size="sm">{currentClinic?.addressLine2}</Text>
            <Text size="sm">{`${currentClinic?.city}, ${currentClinic?.state} ${currentClinic?.postalCode}`}</Text>
          </Flex>
          <Flex direction="column" my={2}>
            <Text size="sm">{currentClinic?.clinicEmail}</Text>
            <Link size="sm" href={currentClinic?.websiteUrl || ''}>
              Visit Website
            </Link>
          </Flex>
        </Flex>
        <Flex direction="column" alignItems="flex-end">
          <Text mt={4} mb={3} fontWeight="bold">
            Bill To:
          </Text>
          <Text size="sm" my={2}>
            {petParentName}
          </Text>
          <Text size="sm">{petParent?.email}</Text>
          <Text size="sm">{petParentPhone}</Text>
        </Flex>
      </Flex>
      <Flex my={10} w="100%" justifyContent="space-between">
        <Flex direction="column">
          <Text size="sm" variant="subtle" fontWeight={600}>
            AMOUNT PAID
          </Text>
          <Text variant="subtle">{paymentAmountDisplay}</Text>
        </Flex>
        <Flex direction="column">
          <Text size="sm" variant="subtle" fontWeight={600}>
            DATE PAID
          </Text>
          <Text variant="subtle">{paymentDate ? format(new Date(paymentDate), 'PPP') : ''}</Text>
        </Flex>
        <Flex direction="column">
          <Text size="sm" variant="subtle" fontWeight={600}>
            PAYMENT METHOD
          </Text>
          <HStack alignItems="center" p="1">
            <b>{getCardBrandIcon(`${paymentMethod[0] || ''}`, '20px', '29px')}</b>
            <Text variant="subtle" pb="1">
              -{paymentMethod[1] || ''}
            </Text>
          </HStack>
        </Flex>
      </Flex>
      <Flex direction="column">
        <Text size="sm" variant="subtle" fontWeight={600} mb={2}>
          SUMMARY
        </Text>
        <Flex mt={2} bg="background.subtle" direction="column" width="100%">
          <Flex py={4} px={5} justifyContent="space-between" borderBottomWidth="1px" borderBottomColor="border.default">
            <Text>{`Payment to ${currentClinic?.name || 'clinic'}`}</Text>
            <Text>{totalBeforeFeeDisplay}</Text>
          </Flex>
          <Flex py={4} px={5} justifyContent="space-between" borderBottomWidth="1px" borderBottomColor="border.default">
            <Text>Service Fee</Text>
            <Text>{clientServiceFeeDisplay}</Text>
          </Flex>
          <Flex py={4} px={5} justifyContent="space-between">
            <Text fontWeight={600}>Amount Charged</Text>
            <Text fontWeight={600}>{paymentAmountDisplay}</Text>
          </Flex>
        </Flex>
      </Flex>
      <Flex justifyContent={!!stripeReceiptId ? 'space-between' : 'flex-end'} alignItems="center" mt={6}>
        {!!stripeReceiptId && (
          <Text size="sm">{`Application Name: ${applicationName}, AID: ${fileName}, Account Type: ${accountType}`}</Text>
        )}
        <Button
          variant="secondary"
          iconName="form"
          width="fit-content"
          onClick={handleDownloadPdf}
          size="sm"
          isLoading={isPDFLoading || isDownloading}
        >
          Print or Download PDF
        </Button>
      </Flex>
    </Flex>
  );
};

export default NonItemizedReceipt;
